/**
 * MandalaRing
 * Decorative concentric mandala used as a background accent.
 * Rotation is handled entirely in CSS (index.css) via .mandala-spin and .mandala-spin-reverse.
 * The two animated groups each have their own wrapping <svg> so transform-origin works
 * predictably without needing transform-box hacks on <g> elements.
 */
const MandalaRing = ({ size = 480, className = "" }) => {
  const cx = size / 2;
  const cy = size / 2;

  // Outer ring — 3 concentric petal layers
  const outerLayers = [
    { count: 8,  r: size * 0.38, ps: size * 0.055 },
    { count: 12, r: size * 0.28, ps: size * 0.040 },
    { count: 16, r: size * 0.20, ps: size * 0.028 },
  ];

  // Inner lotus — 6 petals
  const innerPetals = Array.from({ length: 6 }, (_, i) => {
    const angle = (i / 6) * 360;
    const rad   = (angle * Math.PI) / 180;
    const r     = size * 0.10;
    return { angle, px: cx + r * Math.cos(rad), py: cy + r * Math.sin(rad) };
  });

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      width={size}
      height={size}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
      style={{ overflow: "visible" }}
    >
      {/*
        Outer group — slow clockwise spin.
        We use a <g> with style.animation directly so the transform-origin
        is relative to the SVG coordinate centre, which is reliable cross-browser.
      */}
      <g
        style={{
          transformOrigin: `${cx}px ${cy}px`,
          animation: "mandala-spin-slow 80s linear infinite",
        }}
      >
        {outerLayers.map(({ count, r, ps }, layerIdx) =>
          Array.from({ length: count }, (_, i) => {
            const angle = (i / count) * 360;
            const rad   = (angle * Math.PI) / 180;
            const px    = cx + r * Math.cos(rad);
            const py    = cy + r * Math.sin(rad);
            return (
              <ellipse
                key={`o-${layerIdx}-${i}`}
                cx={px} cy={py}
                rx={ps} ry={ps * 0.42}
                transform={`rotate(${angle} ${px} ${py})`}
                fill="#f97316"
                opacity={0.07 - layerIdx * 0.015}
              />
            );
          })
        )}
        {/* Guide circles */}
        {outerLayers.map(({ r }, i) => (
          <circle key={`c-${i}`} cx={cx} cy={cy} r={r}
            stroke="#f97316" strokeWidth="0.6"
            opacity={0.08 - i * 0.018} strokeDasharray="3 5" />
        ))}
      </g>

      {/* Inner group — slow counter-clockwise spin */}
      <g
        style={{
          transformOrigin: `${cx}px ${cy}px`,
          animation: "mandala-spin-reverse 55s linear infinite",
        }}
      >
        {innerPetals.map(({ angle, px, py }) => (
          <ellipse
            key={`ip-${angle}`}
            cx={px} cy={py}
            rx={size * 0.040} ry={size * 0.016}
            transform={`rotate(${angle} ${px} ${py})`}
            fill="#fbbf24" opacity="0.09"
          />
        ))}
        <circle cx={cx} cy={cy} r={size * 0.06}
          stroke="#fbbf24" strokeWidth="0.8" opacity="0.09" />
        <circle cx={cx} cy={cy} r={size * 0.024}
          fill="#f97316" opacity="0.08" />
      </g>

      {/* Outermost static dashed ring */}
      <circle cx={cx} cy={cy} r={size * 0.47}
        stroke="#f97316" strokeWidth="0.5" opacity="0.05" strokeDasharray="2 8" />
    </svg>
  );
};

export default MandalaRing;
